import { CreateInspectionResponse } from '@services';
import {
  clearAnswerDraft,
  isDraftStorageAvailable,
  loadAnswerDraft,
  saveAnswerDraft,
  sweepStaleDrafts,
} from '@services';
import { useStore } from '@stores';
import { useCallback, useEffect, useRef, useState } from 'react';

export type InspectionSaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface IInspectionAutosaveHook {
  state: InspectionSaveState;
  lastSavedAt?: Date;
  /** Answers that have not reached the server, including anything a failed save is holding on to. */
  pendingCount: number;
  hasUnsaved: boolean;
  /**
   * False where the browser will not keep a local copy - site data blocked, or the quota gone. The
   * work then lives only in this tab, which is worth saying before someone spends half an hour on it.
   */
  storageAvailable: boolean;
  /**
   * Answers found on this device from an earlier session, keyed by checklist item. Undefined until
   * storage has been read - callers wait for it before filling the form, or they would paint the
   * server's version and then jump.
   */
  drafts?: Map<number, CreateInspectionResponse>;
  queue: (subCategoryItemId: number, values: CreateInspectionResponse, immediate?: boolean) => void;
  /** Resolves false if anything is still waiting to go after the attempt. */
  flush: () => Promise<boolean>;
  /**
   * Throws the queue away, on the device as well as in memory. Only for someone who has said they
   * want these changes gone - otherwise the draft would come back on the next open, and a button
   * saying discard would not have discarded anything.
   */
  discard: () => void;
}

/** How long to sit on a note before sending it, so a sentence is one save and not thirty. */
const DEBOUNCE_MS = 2000;

/**
 * Something is queued and the connection is gone. The browser fires `online` when an interface comes
 * back, but not when a server that was down comes back up, so a slow heartbeat covers the rest.
 */
const RETRY_MS = 30000;

/**
 * Saves checklist items on their own, as they are answered, and keeps what it could not send.
 *
 * A whole inspection used to go up on every click of Save - 45 rows and every photo, touched or not -
 * which is what timed out, and what made people press Save after each field in the first place. One
 * item is a couple of hundred bytes, so a full checklist costs less over twenty minutes than one of
 * the old saves.
 *
 * What has not gone up is written to the device as well as held in memory. A connection can be gone
 * for hours, and a queue that only lives in the tab is one refresh, one crash or one expired session
 * away from half a morning of work.
 *
 * Saves are chained rather than fired in parallel: two writes to the same item overlapping is how the
 * later one loses. The API keys a response on (inspection, item), so a resend is an update, never a
 * second row - which is what makes retrying safe.
 */
export const useInspectionAutosave = (inspectionId: string | undefined): IInspectionAutosaveHook => {
  const { inspectionStore } = useStore();

  const [state, setState] = useState<InspectionSaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | undefined>();
  const [pendingCount, setPendingCount] = useState(0);
  const [drafts, setDrafts] = useState<Map<number, CreateInspectionResponse> | undefined>();
  const [storageAvailable, setStorageAvailable] = useState(true);

  const pendingRef = useRef<Map<number, CreateInspectionResponse>>(new Map());
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const chainRef = useRef<Promise<void>>(Promise.resolve());
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      timerRef.current && clearTimeout(timerRef.current);
    };
  }, []);

  /** Mirrors the queue onto the device. Always the whole set, so a partial write cannot half-apply. */
  const persist = useCallback(() => {
    if (!inspectionId) return;

    void saveAnswerDraft(inspectionId, Array.from(pendingRef.current.values()));
  }, [inspectionId]);

  const sync = useCallback(() => {
    if (mountedRef.current) setPendingCount(pendingRef.current.size);
  }, []);

  // Pick up anything left behind by an earlier session before the form is filled in.
  useEffect(() => {
    if (!inspectionId) {
      setDrafts(undefined);
      return;
    }

    let cancelled = false;

    pendingRef.current = new Map();
    setDrafts(undefined);

    // Opening an inspection is the only moment the app reliably touches this storage, so the tidy-up
    // rides along. It runs once a session and skips itself after that.
    void sweepStaleDrafts();

    void isDraftStorageAvailable().then((available) => {
      if (!cancelled) setStorageAvailable(available);
    });

    loadAnswerDraft(inspectionId).then((saved) => {
      if (cancelled) return;

      saved.forEach((answer) => pendingRef.current.set(answer.subCategoryItemId, answer));

      setDrafts(new Map(pendingRef.current));
      sync();
    });

    return () => {
      cancelled = true;
    };
  }, [inspectionId, sync]);

  const flush = useCallback(async (): Promise<boolean> => {
    timerRef.current && clearTimeout(timerRef.current);

    chainRef.current = chainRef.current.then(async () => {
      if (!inspectionId || pendingRef.current.size === 0) return;

      const batch = Array.from(pendingRef.current.values());
      pendingRef.current.clear();

      mountedRef.current && setState('saving');

      try {
        await inspectionStore.createResponses({ id: inspectionId, responses: batch });

        // Only once the server has it. Clearing the draft before this point is how a failed save
        // would quietly become a lost one.
        if (pendingRef.current.size === 0) await clearAnswerDraft(inspectionId);
        else persist();

        if (!mountedRef.current) return;

        setLastSavedAt(new Date());
        setState(pendingRef.current.size > 0 ? 'saving' : 'saved');
      } catch {
        // Put the work back rather than dropping it, but never over the top of something newer the
        // user has typed since. The next save - or Done, or the connection returning - carries it.
        batch.forEach((item) => {
          if (!pendingRef.current.has(item.subCategoryItemId))
            pendingRef.current.set(item.subCategoryItemId, item);
        });

        persist();

        mountedRef.current && setState('error');
      } finally {
        sync();
      }
    });

    await chainRef.current;

    return pendingRef.current.size === 0;
  }, [inspectionId, inspectionStore, persist, sync]);

  const queue = useCallback(
    (subCategoryItemId: number, values: CreateInspectionResponse, immediate = false) => {
      pendingRef.current.set(subCategoryItemId, { ...values, subCategoryItemId });

      persist();
      sync();

      timerRef.current && clearTimeout(timerRef.current);

      if (immediate) {
        void flush();
        return;
      }

      timerRef.current = setTimeout(() => void flush(), DEBOUNCE_MS);
    },
    [flush, persist, sync]
  );

  // Try again when the connection comes back, and on a slow heartbeat for everything the browser
  // does not announce - a server that was down, a captive portal, a VPN dropping out.
  useEffect(() => {
    const retry = () => {
      if (pendingRef.current.size > 0) void flush();
    };

    window.addEventListener('online', retry);
    const heartbeat = setInterval(retry, RETRY_MS);

    return () => {
      window.removeEventListener('online', retry);
      clearInterval(heartbeat);
    };
  }, [flush]);

  const discard = useCallback(() => {
    timerRef.current && clearTimeout(timerRef.current);

    pendingRef.current.clear();

    if (inspectionId) void clearAnswerDraft(inspectionId);

    setState('idle');
    sync();
  }, [inspectionId, sync]);

  return {
    state,
    lastSavedAt,
    pendingCount,
    hasUnsaved: pendingCount > 0,
    storageAvailable,
    drafts,
    queue,
    flush,
    discard,
  };
};
